import { createColumnHelper } from '@tanstack/react-table';

import { Order } from 'types/order';
import { Status } from 'types/items';
import { convertUTCToLocal } from 'utils/dateUtils';

const col = createColumnHelper<Order>();

export type SkuItemsById = Record<string, number | undefined>;

/** Single-detail orders show that SKU's name; multi-detail orders don't name one. */
const skuNameFor = (
  order: Order,
  getSkuNameById: (skuID?: string) => string,
) => {
  const details = order?.details;
  const length = details?.length;
  const sku = length === 1 ? details[0]?.sku : '';

  return length > 1 ? `2+ SKU's` : getSkuNameById(sku);
};

const cupsCountFor = (order: Order, skuItemsById: SkuItemsById) =>
  order?.details?.reduce((skuAdded, orderDetails) => {
    const itemsQtyInCase = skuItemsById[orderDetails.sku] ?? 1;
    return skuAdded + orderDetails.casesCount * itemsQtyInCase;
  }, 0);

/**
 * getSkuNameById/skuItemsById are derived from the useSKU query in
 * DeliveriesTab, so they're passed in rather than looked up inside a plain
 * accessor function (same shape as employeesColumns' getWashHubByID).
 */
export const buildDeliveriesColumns = (
  getSkuNameById: (skuID?: string) => string,
  skuItemsById: SkuItemsById,
) => [
  col.accessor((order) => order?._id, {
    id: 'id',
    header: 'ID',
    minSize: 150,
    size: 200,
  }),
  col.accessor((order) => skuNameFor(order, getSkuNameById), {
    id: 'skuName',
    header: 'SKU name',
    minSize: 150,
    size: 200,
  }),
  col.accessor((order) => convertUTCToLocal(order?.deliveryAt), {
    id: 'deliveryAt',
    header: 'Delivery Date',
    minSize: 110,
    size: 150,
  }),
  col.accessor((order) => order?.site?.name || '-', {
    id: 'site',
    header: 'Site',
    minSize: 120,
    size: 160,
  }),
  col.accessor((order) => Status[order?.status || 0], {
    id: 'status',
    header: 'Status',
    minSize: 90,
    size: 100,
    meta: { align: 'center' },
  }),
  col.accessor((order) => cupsCountFor(order, skuItemsById), {
    id: 'cupsCount',
    header: 'Cups Count',
    minSize: 90,
    size: 100,
    meta: { align: 'center' },
  }),
];
